/**
 * BatchAnalyzer — Tier-1 Pro tool #2.
 * Pick multiple saved recipes, tweak shared assumptions, get a
 * side-by-side cost/throughput table with best-cost + best-throughput
 * winners called out.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Layers, Sparkles, Loader2, Trophy, Zap, DollarSign,
} from 'lucide-react';
import ProTierGate from '../components/tools/ProTierGate';
import PageHero from '../components/PageHero';

const ASSUMPTION_FIELDS = [
  ['electricity_cost_per_kwh', 'Electricity ($/kWh)',  0.01],
  ['machine_wattage_w',        'Machine wattage (W)',  10],
  ['labor_rate_per_hour',      'Labor ($/hr)',         0.5],
  ['labor_hours_per_batch',    'Labor hours / batch',  0.25],
  ['packaging_cost_per_batch', 'Packaging ($/batch)',  0.5],
  ['ingredient_cost_per_kg',   'Ingredients ($/kg)',   0.5],
  ['yield_loss_pct',           'Yield loss (%)',       1],
];

const Inner = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [assumptions, setAssumptions] = useState(null);
  const [picked, setPicked] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rlist, defs] = await Promise.all([
          axios.get(`${API}/tools/recipe-builder`),
          axios.get(`${API}/tools/batch-analyzer/defaults`),
        ]);
        setRecipes(Array.isArray(rlist.data) ? rlist.data : []);
        setAssumptions(defs.data);
        // Honor `?recipe_ids=a,b,c` deep link from Recipe Builder.
        const raw = params.get('recipe_ids');
        if (raw) {
          const ids = raw.split(',').filter(Boolean);
          if (ids.length) {
            const seed = {}; ids.forEach(id => { seed[id] = true; });
            setPicked(seed);
          }
        }
      } catch (err) {
        // swallow (Pro gate handles messaging)
      }
    })();
  }, [API, params]);

  const pickedIds = useMemo(() => Object.keys(picked).filter(k => picked[k]), [picked]);
  const toggle = (id) => setPicked(p => ({ ...p, [id]: !p[id] }));

  const runEstimate = async () => {
    if (pickedIds.length === 0) return toast.error('Pick at least one recipe');
    if (!assumptions) return;
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/tools/batch-analyzer/estimate`, {
        recipe_ids: pickedIds,
        assumptions,
      });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Estimate failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="batch-analyzer-inner">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="ba-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
        <div className="text-[10px] uppercase tracking-widest font-extrabold text-primary">Tier-1 · Pro Tool</div>
      </div>
      <PageHero
        icon={<Layers className="w-8 h-8" />}
        eyebrow="Batch Analyzer"
        title="Compare recipes side-by-side"
        subtitle="Pick recipes, tweak your shop's operating costs, and see cost/kg + throughput drift across your library."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: pickers + assumptions */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Recipes to compare</CardTitle></CardHeader>
            <CardContent className="space-y-1" data-testid="ba-recipe-picker">
              {recipes.length === 0 && (
                <div className="text-sm text-muted-foreground italic">
                  No saved recipes yet. Head to Recipe Builder first.
                </div>
              )}
              {recipes.map((r) => (
                <label key={r.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-muted cursor-pointer" data-testid={`ba-pick-${r.id}`}>
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!picked[r.id]}
                    onChange={() => toggle(r.id)}
                  />
                  <div>
                    <div className="text-sm font-bold text-foreground">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.ingredients?.length || 0} ingredients · {Number(r.analysis?.predicted_total_cycle_hours || 0).toFixed(0)}h cycle
                    </div>
                  </div>
                </label>
              ))}
            </CardContent>
          </Card>

          {assumptions && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Operating assumptions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {ASSUMPTION_FIELDS.map(([k, label, step]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</label>
                    <Input
                      type="number"
                      step={step}
                      value={assumptions[k]}
                      onChange={(e) => setAssumptions({ ...assumptions, [k]: Number(e.target.value) })}
                      className="mt-1"
                      data-testid={`ba-assumption-${k}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Button onClick={runEstimate} disabled={running || pickedIds.length === 0} className="w-full rounded-full font-extrabold bg-primary text-white" data-testid="ba-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {running ? 'Estimating…' : `Estimate ${pickedIds.length} recipe${pickedIds.length === 1 ? '' : 's'}`}
          </Button>
        </div>

        {/* Right: comparison table */}
        <div className="lg:col-span-2 space-y-4">
          {!result && (
            <Card><CardContent className="p-6 text-sm text-muted-foreground italic text-center">Pick recipes and hit Estimate to see the comparison table.</CardContent></Card>
          )}
          {result && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl p-3 bg-primary/10 border border-primary/30" data-testid="ba-agg-cost">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Aggregate cost</div>
                  <div className="mt-1 text-2xl font-extrabold text-foreground">${result.aggregate_total_cost.toFixed(2)}</div>
                </div>
                <div className="rounded-xl p-3 bg-emerald-50 border border-emerald-200" data-testid="ba-agg-output">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Aggregate output</div>
                  <div className="mt-1 text-2xl font-extrabold text-foreground">{result.aggregate_output_kg.toFixed(2)} kg</div>
                </div>
                <div className="rounded-xl p-3 bg-amber-50 border border-amber-200" data-testid="ba-agg-hours">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Aggregate cycle hrs</div>
                  <div className="mt-1 text-2xl font-extrabold text-foreground">{result.aggregate_hours.toFixed(0)} h</div>
                </div>
              </div>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Per-batch breakdown</CardTitle></CardHeader>
                <CardContent className="overflow-x-auto p-0">
                  <table className="w-full text-sm" data-testid="ba-comparison-table">
                    <thead className="bg-muted/50">
                      <tr>
                        {['Recipe', 'Cycle h', 'Output kg', 'Total $', '$/kg out', 'kg/day', '🥇'].map(h => (
                          <th key={h} className="p-2 text-left font-bold text-[11px] uppercase tracking-widest text-muted-foreground">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.lines.map((l) => (
                        <tr key={l.recipe_id} className="border-t border-border" data-testid={`ba-line-${l.recipe_id}`}>
                          <td className="p-2 font-semibold">{l.recipe_name}</td>
                          <td className="p-2">{l.cycle_hours.toFixed(1)}</td>
                          <td className="p-2">{l.output_weight_kg.toFixed(2)}</td>
                          <td className="p-2">${l.total_cost.toFixed(2)}</td>
                          <td className="p-2">${l.cost_per_kg_output.toFixed(2)}</td>
                          <td className="p-2">{l.throughput_kg_per_day.toFixed(2)}</td>
                          <td className="p-2 whitespace-nowrap">
                            {l.recipe_id === result.best_cost_per_kg_recipe_id && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-bold" title="Best cost per kg"><DollarSign className="w-3 h-3" /> Best $</span>
                            )}
                            {l.recipe_id === result.best_throughput_recipe_id && (
                              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-[10px] font-bold" title="Best throughput"><Zap className="w-3 h-3" /> Fastest</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const BatchAnalyzer = () => (
  <ProTierGate toolName="Batch Analyzer" tagline="Compare cost and throughput across your recipe library in one click." data_testid_prefix="batch-analyzer-gate">
    <Inner />
  </ProTierGate>
);
export default BatchAnalyzer;
